import type { Listeners, TEventSource } from '../helpers';
import { controllerHelpers } from '../helpers';
import {
  emailPlansEventBus,
  promoCodesEventBus,
  type EmailPlanChangedPayload,
  type InboundPromoCodesManager,
  type OrderSummaryManager,
  type PromoCodesManager,
} from '../managers';

export class PromoCodesController {
  public listeners: Listeners;

  constructor(
    private orderSummaryManager: OrderSummaryManager,
    private promoCodesManager: PromoCodesManager,
    private inboundPromoCodesManager?: InboundPromoCodesManager
  ) {
    const eventConnections = [
      promoCodesEventBus.bindTo('promoCodeApplySuccess', this.promoCodeAdded),
      promoCodesEventBus.bindTo('promoCodeRemove', this.promoCodeRemoved),
      emailPlansEventBus.bindTo('emailPlanChanged', this.onEmailPlanChanged),
    ];

    this.listeners = controllerHelpers.createListeners(eventConnections);
  }

  private revalidatePromoCodesForPlanChange = async (planId: number) => {
    const [payloadPromoCodes, inboundPromoCodes] = await Promise.all([
      this.orderSummaryManager.getNewPromotionCodeNamesWhenReady(),
      this.inboundPromoCodesManager
        ? this.inboundPromoCodesManager.getInboundPromoCodesWhenReady()
        : Promise.resolve([]),
    ]);

    await this.orderSummaryManager.clearAllPromoCodesForPlanChangeWhenReady();
    await this.promoCodesManager.preparePromoRevalidationAfterPlanChangeWhenReady(
      planId,
      payloadPromoCodes,
      inboundPromoCodes
    );
  };

  private onEmailPlanChanged = async (
    { planId, previousPlanId }: EmailPlanChangedPayload,
    { source }: { source: TEventSource }
  ) => {
    if (source !== 'user' || previousPlanId === null || previousPlanId === planId) {
      return;
    }

    await this.revalidatePromoCodesForPlanChange(planId);
  };

  private promoCodeAdded = async (code: string) => {
    const api = await this.orderSummaryManager.getApiWhenReady();

    api.addPromoCode(code);
  };

  private promoCodeRemoved = async (code: string) => {
    const api = await this.orderSummaryManager.getApiWhenReady();

    api.removePromoCode(code);
  };
}
